import React, { useEffect } from 'react';
import { Row, Col, Collapse, Form, FormGroup, Input, Label } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import DatePicker from 'components/layout/forms/DatePicker';
import TextWithDateInputField from 'components/layout/forms/TextWithDateInputField';
import maximize from 'assets/maximize.svg';
import minimize from 'assets/minimize.svg';
import NumberInputField from 'components/layout/forms/NumberInputField';
import PrimaryButton from 'components/layout/buttons/PrimaryButton';
import SearchDropdown from 'components/layout/forms/SearchDropdown';
import SelectInputFieldControlled from 'components/layout/forms/SelectInputFieldControlled';
import SelectInputTranslated from 'components/layout/forms/SelectInputTranslated';
import TextInputField from 'components/layout/forms/TextInputField';
import translations from 'services/translations/translations';
import UpdatedField from 'components/layout/forms/UpdatedField';
import UserSelect from 'components/layout/forms/UserSelect';
import { APPROVED, isConvertedStatus } from 'services/enums/offers/Statuses';
import { OPPORTUNITY } from 'services/enums/offers/offerTypes';
import { NO } from 'services/enums/offers/collateralTypes';
import {
  DEFAULT_LANGUAGE,
  LANGUAGE_NAMES,
  SUPPORTED_LANGUAGES,
  isSupportedLanguage,
} from 'services/translations/languages';

import {
  fetchBuildingTypes,
  fetchCollateralTypes,
  fetchContractTypes,
  fetchEmployeeInformationTypes,
  fetchWayOfReceivingTypes,
  fetchWorkTypes,
} from 'store/projects/actions';

import {
  showDesigners,
  showOptionsFirstBlank,
} from 'components/offers/OfferSelectOptions';
import OfferInfoRowFields from './OfferInfoRowFields';

import IOffer from './IOffer';
import OfferInvoicingInfo from './OfferInvoicingInfo';
import OfferTextField from './OfferTextField';
import Status from './fields/Status';

import 'style/layout/Icons/CollapseIcon.css';
import 'style/offers/OfferBasicInfo.css';

// Additional offers are a separate kind of offer and cannot be changed to or from
const CHANGEABLE_OFFER_TYPES = [
  'offer',
  'preliminary-offer',
  'material-offer',
  'cost-estimate',
];

const OfferBasicInfo = ({
  toggleOffer,
  offerIsOpen,
  formState,
  handleFormState,
  customerOffice,
  handleCustomerOffice,
  searchCustomerOffice,
  startDate,
  onChangeDate,
  endDate,
  siteEndDate,
  siteStartDate,
  offerId,
  requestedSubmissionDate,
  projectContacts,
  saveOffer,
  customers,
  paymentTerms,
  handlePaymentTerm,
  paymentTerm,
  initialStatus,
  locked,
  organizationUnitField,
  readOnly,
  typeCanBeChanged,
  additionalRecipients,
  signatureUser,
  handleSignatureUser,
}: {
  toggleOffer: any;
  offerIsOpen: boolean;
  formState: IOffer;
  handleFormState: any;
  customerOffice: any;
  handleCustomerOffice: any;
  searchCustomerOffice: any;
  startDate: any;
  onChangeDate: any;
  endDate: any;
  // The start and end of the whole building project, null when not known
  siteEndDate: Date | null;
  siteStartDate: Date | null;
  // Undefined until the offer is saved
  offerId?: number;
  // The date the customer wishes to have the offer by
  requestedSubmissionDate: Date | null;
  // The contacts of the project besides the customers (e.g. architects), rows with roles
  projectContacts?: JSX.Element | null;
  saveOffer: any;
  customers: any;
  paymentTerms: any;
  handlePaymentTerm: any;
  paymentTerm: any;
  initialStatus: null | string;
  locked: boolean;
  // Choosing the department of the offer, the department is only shown without it
  organizationUnitField?: JSX.Element | null;
  // The user can only read the offers of the department, not even change the status
  readOnly?: boolean;
  typeCanBeChanged: boolean;
  additionalRecipients?: JSX.Element | null;
  signatureUser: any;
  handleSignatureUser: any;
}): JSX.Element | null => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation([
    translations.offers,
    translations.common,
    translations.products,
    translations.userManagement,
  ]);

  // A new offer is printed in the language of the user unless another one is chosen
  const getNewOfferLanguage = () =>
    isSupportedLanguage(i18n.language) ? i18n.language : DEFAULT_LANGUAGE;

  useEffect(() => {
    dispatch(fetchBuildingTypes());
    dispatch(fetchCollateralTypes());
    dispatch(fetchContractTypes());
    dispatch(fetchEmployeeInformationTypes());
    dispatch(fetchWayOfReceivingTypes());
    dispatch(fetchWorkTypes());
  }, []);

  const buildingTypes = useSelector(
    (state: any) => state.projects.buildingTypes
  );

  const collateralTypes = useSelector(
    (state: any) => state.projects.collateralTypes
  );

  const contractTypes = useSelector(
    (state: any) => state.projects.contractTypes
  );

  const employeeInformationTypes = useSelector(
    (state: any) => state.projects.employeeInformationTypes
  );

  const wayOfReceivingTypes = useSelector(
    (state: any) => state.projects.wayOfReceivingTypes
  );

  const workTypes = useSelector((state: any) => state.projects.workTypes);

  // An approved offer and a converted opportunity are final
  const offerIsApproved = () =>
    initialStatus === APPROVED || isConvertedStatus(initialStatus);

  const offerIsLocked = () => locked || readOnly === true;

  // A sales opportunity: basic information, dates and contacts only
  const isOpportunity = formState.offerType === OPPORTUNITY;

  const noCollateral = () => formState.collateral === NO;

  // The PDF fields are three a row, four when the offer has own rows of its own
  const pdfFieldWidth = (formState.infoRows ?? []).length > 0 ? 3 : 4;

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div onClick={toggleOffer} className="header-text title-spacing">
        {offerIsOpen ? (
          <img src={minimize} alt="toggle-offer" className="collapse-icon" />
        ) : (
          <img src={maximize} alt="toggle-offer" className="collapse-icon" />
        )}
        {t(`${translations.offers}:Basic Info`).toUpperCase()}
      </div>
      <Collapse isOpen={offerIsOpen}>
        <Form>
          <Row>
            <Col md={4}>
              <SelectInputTranslated
                // An opportunity becomes an offer only by closing it as converted
                disabled={!typeCanBeChanged || isOpportunity}
                key="offerType"
                label={t(`${translations.offers}:Offer Type`)}
                name="offerType"
                onChange={handleFormState}
                options={isOpportunity ? [OPPORTUNITY] : CHANGEABLE_OFFER_TYPES}
                required
                translationPath={`${translations.offers}:types`}
                value={formState.offerType}
              />
            </Col>
            <Col md={2}>
              {/* The standard texts of the PDF are printed in this language */}
              <FormGroup>
                <Label className="label" for="offerLanguage">
                  {t(`${translations.offers}:Offer Language`)}
                </Label>
                <Input
                  className="text-field"
                  disabled={offerIsLocked()}
                  id="offerLanguage"
                  name="language"
                  onChange={handleFormState}
                  type="select"
                  value={formState.language || getNewOfferLanguage()}
                >
                  {SUPPORTED_LANGUAGES.map((language) => (
                    <option key={language} value={language}>
                      {LANGUAGE_NAMES[language]}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md={2}>
              <FormGroup>
                <Label className="label" for="requestedSubmissionDate">
                  {t(`${translations.offers}:Requested submission date`)}
                </Label>
                <DatePicker
                  disabled={offerIsLocked()}
                  id="requestedSubmissionDate"
                  isClearable={!offerIsLocked()}
                  onChange={(date) => onChangeDate(date, 'requestedSubmission')}
                  selected={requestedSubmissionDate}
                />
              </FormGroup>
            </Col>
            <Col md={4}>
              {typeCanBeChanged && !isOpportunity ? (
                <p className="offer-type-info">
                  {t(`${translations.offers}:Offer type info`)}
                </p>
              ) : null}
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label className="label">
                  {t(`${translations.offers}:Customer Office`)}
                </Label>
                <SearchDropdown
                  value={customerOffice}
                  required
                  placeholder={t(`${translations.common}:Search`)}
                  className="text-field search-field"
                  name="customerOffice"
                  onChange={handleCustomerOffice}
                  loadOptions={searchCustomerOffice}
                  disabled={offerIsLocked()}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <SelectInputFieldControlled
                value={formState.customer}
                label={t(`${translations.offers}:Customer`)}
                required
                id="customer"
                name="customer"
                onChange={handleFormState}
                options={customers ? showDesigners(customers) : ''}
                disabled={offerIsLocked()}
              />
            </Col>
          </Row>
          {additionalRecipients}
          <Row>
            <Col>
              <TextInputField
                value={formState.target}
                label={t(`${translations.offers}:Target`)}
                required
                id="target"
                name="target"
                onChange={handleFormState}
                disabled={offerIsLocked()}
              />
            </Col>
            <Col md={3}>
              <SelectInputFieldControlled
                value={formState.contractType}
                label={t(`${translations.offers}:Contract Type`)}
                required
                id="contractType"
                name="contractType"
                onChange={handleFormState}
                options={
                  contractTypes
                    ? showOptionsFirstBlank(
                        'contractTypes',
                        contractTypes,
                        translations.offers,
                        ':',
                        t
                      )
                    : ''
                }
                disabled={offerIsLocked()}
              />
            </Col>
            <Col md={3}>
              <SelectInputFieldControlled
                value={formState.jobType}
                label={t(`${translations.offers}:Job Type`)}
                required
                id="jobType"
                name="jobType"
                onChange={handleFormState}
                options={
                  workTypes
                    ? showOptionsFirstBlank(
                        'workTypes',
                        workTypes,
                        translations.offers,
                        ':',
                        t
                      )
                    : ''
                }
                disabled={offerIsLocked()}
              />
            </Col>
            <Col md={3}>
              <SelectInputFieldControlled
                value={formState.buildingType}
                // Named as in the offers list; the projects keep "Building Type"
                label={t(`${translations.offers}:list.Building type`)}
                required
                id="buildingType"
                name="buildingType"
                onChange={handleFormState}
                options={
                  buildingTypes
                    ? showOptionsFirstBlank(
                        'buildingTypes',
                        buildingTypes,
                        `${translations.offers}:buildingTypes`,
                        ':',
                        t
                      )
                    : ''
                }
                disabled={offerIsLocked()}
              />
            </Col>
          </Row>
          <Row>
            <Col md={3}>
              <TextInputField
                value={formState.address}
                label={t(`${translations.offers}:Address`)}
                required
                id="address"
                name="address"
                onChange={handleFormState}
                disabled={offerIsLocked()}
              />
            </Col>
            <Col md={3}>
              <TextInputField
                value={formState.zipCode}
                label={t(`${translations.offers}:ZIP Code`)}
                required
                id="zipCode"
                name="zipCode"
                onChange={handleFormState}
                disabled={offerIsLocked()}
              />
            </Col>
            <Col md={3}>
              <TextInputField
                value={formState.city}
                label={t(`${translations.offers}:City`)}
                required
                id="city"
                name="city"
                onChange={handleFormState}
                disabled={offerIsLocked()}
              />
            </Col>
            <Col md={3}>
              <TextInputField
                value={formState.country}
                label={t(`${translations.offers}:Country`)}
                required
                id="country"
                name="country"
                onChange={handleFormState}
                disabled={offerIsLocked()}
              />
            </Col>
          </Row>
          <Row>
            {/* The whole building project, then the work of the offer (the contract) */}
            <Col md={3}>
              <FormGroup>
                <Label className="label">
                  {t(`${translations.offers}:Site start`)}
                </Label>
                <DatePicker
                  showWeekNumbers
                  isClearable={!offerIsLocked()}
                  selected={siteStartDate}
                  onChange={(date) => onChangeDate(date, 'siteStart')}
                  id="siteStartDate"
                  disabled={offerIsLocked()}
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label className="label">
                  {t(`${translations.offers}:Site end`)}
                </Label>
                <DatePicker
                  showWeekNumbers
                  isClearable={!offerIsLocked()}
                  selected={siteEndDate}
                  onChange={(date) => onChangeDate(date, 'siteEnd')}
                  id="siteEndDate"
                  minDate={siteStartDate ?? undefined}
                  disabled={offerIsLocked()}
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label className="label">
                  {t(`${translations.offers}:Contract start`)}
                </Label>
                <DatePicker
                  showWeekNumbers
                  selected={startDate}
                  onChange={(date) => onChangeDate(date, 'start')}
                  id="startDate"
                  required
                  disabled={offerIsLocked()}
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label className="label">
                  {t(`${translations.offers}:Contract end`)}
                </Label>
                <DatePicker
                  showWeekNumbers
                  selected={endDate}
                  onChange={(date) => onChangeDate(date, 'end')}
                  id="endDate"
                  required
                  disabled={offerIsLocked()}
                />
              </FormGroup>
            </Col>
          </Row>
          {projectContacts}
          {/* An opportunity has no contract terms, invoicing or PDF */}
          {isOpportunity ? null : (
            <>
              <Row>
                <Col>
                  <TextInputField
                    disabled
                    value={formState.orderNumber}
                    label={t(`${translations.offers}:Order Number`)}
                    required
                    id="orderNumber"
                    name="orderNumber"
                    onChange={handleFormState}
                  />
                </Col>
                <Col>
                  <TextInputField
                    value={formState.customerMark}
                    label={t(`${translations.offers}:customerMark`)}
                    required
                    id="customerMark"
                    name="customerMark"
                    onChange={handleFormState}
                    disabled={offerIsLocked()}
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  <SelectInputFieldControlled
                    value={formState.collateral}
                    label={t(`${translations.offers}:Collateral`)}
                    required
                    id="collateral"
                    name="collateral"
                    onChange={handleFormState}
                    options={
                      collateralTypes
                        ? showOptionsFirstBlank(
                            'collateral',
                            collateralTypes,
                            translations.offers,
                            ':',
                            t
                          )
                        : ''
                    }
                    disabled={offerIsLocked()}
                  />
                </Col>
                <Col>
                  {noCollateral() ? null : (
                    <NumberInputField
                      value={formState.collateralAmount}
                      label={t(`${translations.offers}:collateralAmount`)}
                      required
                      id="collateralAmount"
                      name="collateralAmount"
                      onChange={handleFormState}
                      disabled={offerIsLocked()}
                    />
                  )}
                </Col>
                <Col>
                  {noCollateral() ? null : (
                    <NumberInputField
                      value={formState.collateralPercentage}
                      label={t(`${translations.offers}:collateralPercentage`)}
                      required
                      id="collateralPercentage"
                      name="collateralPercentage"
                      onChange={handleFormState}
                      disabled={offerIsLocked()}
                    />
                  )}
                </Col>
              </Row>
              <Row>
                <Col>
                  <SelectInputFieldControlled
                    value={formState.employeeInformationType}
                    label={t(`${translations.offers}:employeeInformationType`)}
                    required
                    id="employeeInformationType"
                    name="employeeInformationType"
                    onChange={handleFormState}
                    options={
                      employeeInformationTypes
                        ? showOptionsFirstBlank(
                            'employeeInformationType',
                            employeeInformationTypes,
                            translations.offers,
                            ':',
                            t
                          )
                        : ''
                    }
                    disabled={offerIsLocked()}
                  />
                </Col>
                <Col>
                  <SelectInputFieldControlled
                    value={formState.wayOfReceivingType}
                    label={t(`${translations.offers}:wayOfReceivingType`)}
                    required
                    id="wayOfReceivingType"
                    name="wayOfReceivingType"
                    onChange={handleFormState}
                    options={
                      wayOfReceivingTypes
                        ? showOptionsFirstBlank(
                            'wayOfReceivingType',
                            wayOfReceivingTypes,
                            translations.offers,
                            ':',
                            t
                          )
                        : ''
                    }
                    disabled={offerIsLocked()}
                  />
                </Col>
                <Col>
                  <TextInputField
                    value={formState.worksiteKey}
                    label={t(`${translations.offers}:worksiteKey`)}
                    required
                    id="worksiteKey"
                    name="worksiteKey"
                    onChange={handleFormState}
                    disabled={offerIsLocked()}
                  />
                </Col>
              </Row>
              <div className="subsection">
                <OfferInvoicingInfo
                  formState={formState}
                  handleFormState={handleFormState}
                  paymentTerms={paymentTerms}
                  handlePaymentTerm={handlePaymentTerm}
                  paymentTerm={paymentTerm}
                  locked={offerIsLocked()}
                />
              </div>
              <div className="subsection">
                <Row>
                  <Col>
                    <h3>
                      {t(`${translations.offers}:offer`).toUpperCase()} PDF
                    </h3>
                  </Col>
                </Row>
                {/* The fields of the PDF information table, then the own rows printed
                    after the collaterals: four fields a row when there are own rows */}
                <Row>
                  <Col md={pdfFieldWidth}>
                    <TextInputField
                      value={formState.attachmentsNote}
                      label={t(`${translations.offers}:Attachments`)}
                      required
                      name="attachmentsNote"
                      onChange={handleFormState}
                      disabled={offerIsLocked()}
                    />
                  </Col>
                  <Col md={pdfFieldWidth}>
                    <TextInputField
                      value={formState.cargo}
                      label={t(`${translations.offers}:Cargo`)}
                      required
                      name="cargo"
                      onChange={handleFormState}
                      disabled={offerIsLocked()}
                    />
                  </Col>
                  <Col md={pdfFieldWidth}>
                    {/* A date from the calendar or free text, e.g. 30 pv */}
                    <TextWithDateInputField
                      value={formState.validity}
                      label={t(`${translations.offers}:Validity`)}
                      required
                      name="validity"
                      onChange={handleFormState}
                      disabled={offerIsLocked()}
                    />
                  </Col>
                  <Col md={pdfFieldWidth}>
                    <TextInputField
                      value={formState.deliveryTimeNote}
                      label={t(`${translations.offers}:Delivery time`)}
                      required
                      name="deliveryTimeNote"
                      onChange={handleFormState}
                      disabled={offerIsLocked()}
                    />
                  </Col>
                  <Col md={pdfFieldWidth}>
                    <TextInputField
                      value={formState.invoiceNote}
                      label={t(`${translations.offers}:Invoicing`)}
                      required
                      name="invoiceNote"
                      onChange={handleFormState}
                      disabled={offerIsLocked()}
                    />
                  </Col>
                  <Col md={pdfFieldWidth}>
                    <UserSelect
                      disabled={offerIsLocked()}
                      label={t(`${translations.offers}:Signature`)}
                      onChange={handleSignatureUser}
                      ownOrganizationUnit
                      value={signatureUser}
                    />
                  </Col>
                  <OfferInfoRowFields
                    columnWidth={pdfFieldWidth}
                    disabled={offerIsLocked()}
                    onChange={(infoRows) =>
                      handleFormState({
                        target: { name: 'infoRows', value: infoRows },
                      })
                    }
                    rows={formState.infoRows ?? []}
                  />
                </Row>
                <Row>
                  <Col>
                    <OfferTextField
                      label={t(`${translations.calculationWindow}:Offer text`)}
                      locked={offerIsLocked()}
                      onChange={(offerText) =>
                        handleFormState({
                          target: { name: 'offerText', value: offerText },
                        })
                      }
                      value={formState.offerText}
                    />
                  </Col>
                </Row>
              </div>
            </>
          )}
          <div className="subsection">
            <Row>
              <Col>
                <h3>{t(`${translations.offers}:Status`).toUpperCase()}</h3>
              </Col>
            </Row>
            <Row>
              <Col>
                <Status
                  disabled={readOnly}
                  formState={formState}
                  handleFormState={handleFormState}
                  initialStatus={initialStatus}
                  offerId={offerId}
                  offerType={formState.offerType}
                  sent={formState.sentAt !== null}
                />
              </Col>
              <Col>
                <UpdatedField
                  label={t(`${translations.offers}:Sent`)}
                  updated={formState.sentAt}
                  updatedUser={formState.sentUser}
                />
              </Col>
              <Col>
                <UpdatedField
                  label={t(`${translations.common}:Created`)}
                  updated={formState.created}
                  updatedUser={formState.createdUser}
                />
              </Col>
              <Col>
                <UpdatedField
                  label={t(`${translations.common}:Updated`)}
                  updated={formState.updated}
                  updatedUser={formState.updatedUser}
                />
              </Col>
              <Col>
                {organizationUnitField ?? (
                  <TextInputField
                    disabled
                    label={t(
                      `${translations.userManagement}:Organization unit`
                    )}
                    required
                    value={
                      formState.organizationUnit === null
                        ? ''
                        : formState.organizationUnit.name
                    }
                  />
                )}
              </Col>
            </Row>
            <Row>
              <Col xs={6} />
              <Col xs={6}>
                {offerIsApproved() || readOnly ? null : (
                  <PrimaryButton
                    className="offer-save-button"
                    onClick={() => saveOffer()}
                  >
                    {t(`${translations.common}:Save`).toLocaleUpperCase()}
                  </PrimaryButton>
                )}
              </Col>
            </Row>
          </div>
        </Form>
      </Collapse>
    </>
  );
};

export default OfferBasicInfo;
